import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TvControllerGroup } from '@/lib/schema/tvControllerGroup';
import { useEffect, useState } from 'react';

type GroupChannelGuideDialogProps = {
  group: TvControllerGroup | null;
  channelGuides: { id: string; name: string }[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (group: TvControllerGroup, channelGuideId: string) => Promise<void>;
};

/** One channel guide per parent group: saved on the group and loaded on all
 *  its TVs (subgroups included). The mobile app lists its channels. */
export function GroupChannelGuideDialog({
  group,
  channelGuides,
  onOpenChange,
  onSubmit,
}: GroupChannelGuideDialogProps) {
  const [guideId, setGuideId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (group) setGuideId(group.channelGuideId || '');
  }, [group]);

  return (
    <Dialog open={!!group} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Channel guide — {group?.name}</DialogTitle>
          <DialogDescription>
            The guide is loaded on every TV of this group and its subgroups,
            and is the channel list the mobile app shows after scanning the
            group's QR code.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="group-channel-guide">Channel guide</Label>
          <Select value={guideId} onValueChange={setGuideId}>
            <SelectTrigger id="group-channel-guide">
              <SelectValue placeholder="Select a channel guide" />
            </SelectTrigger>
            <SelectContent>
              {channelGuides.map((guide) => (
                <SelectItem key={guide.id} value={guide.id}>
                  {guide.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!guideId || isSaving}
            onClick={async () => {
              if (!group || !guideId) return;
              setIsSaving(true);
              try {
                await onSubmit(group, guideId);
                onOpenChange(false);
              } catch {
                // save() already toasted the failure; keep the dialog open.
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Assign and load on TVs'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
